import React, { useState } from 'react';

import type { PopoverProps } from '@mui/material';
import { DialogActions, DialogContent, Typography } from '@mui/material';
import { difference } from 'ramda';
import type { ExistingContractType, NewContractType, ReportGridRow } from 'src/components';
import { AlineButton, AlineDialog, AlineTooltip, ContractTypeChip } from 'src/components';
import { ContractTypePopper } from 'src/components/generic/ContractTypePopper';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { StorableWithRoleInfo } from 'src/helpers';
import {
  useCreateContractTypes,
  useGetIsAuthorizedToEditStorable,
  useUpdateDocumentType,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    width: theme.dialogStyles.standardDialogWidth,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  nameSection: {
    marginBottom: theme.spacing(3),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

export interface ContractTypeDocument
  extends
    StorableWithRoleInfo,
    Pick<
      Apollo.Document,
      | 'id'
      | 'coreAiPropertiesExtractionStartedAt'
      | 'coreAiPropertiesExtractionFinishedAt'
      | 'coreAiPropertiesExtractionFailedAt'
      | 'variant'
    > {
  latestVersion: ReportGridRow['latestVersion'];
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
}

interface ContractTypePickerProps {
  document: ContractTypeDocument;
  popoverProps?: Partial<Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>>;
}

export function ContractTypePicker({
  document,
  popoverProps,
}: ContractTypePickerProps): JSX.Element {
  const { classes } = useStyles();
  const {
    anchorEl,
    closeDialog,
    creatingType,
    disabledReason,
    handleChangeType,
    handleCreateNewType,
    newContractType,
    openPopper,
    setAnchorEl,
    setNewContractType,
    setOpenPopper,
  } = useContractTypePicker({ document });

  return (
    <>
      <ContractTypeChip
        ref={setAnchorEl}
        contractType={document.type}
        onClick={e => {
          e.stopPropagation();
          setOpenPopper(true);
        }}
        onDelete={async () => {
          await handleChangeType(null);
        }}
      />
      <ContractTypePopper
        suggestNewOption
        anchorEl={anchorEl}
        disabledReason={disabledReason}
        open={openPopper}
        popoverProps={popoverProps}
        value={document.type ? [document.type] : []}
        onChange={async (_e, contractTypes) => {
          setOpenPopper(false);

          const newContractType = difference(
            contractTypes,
            document.type ? [document.type] : []
          ).at(0);
          if (newContractType?.__typename === 'NewContractType')
            return setNewContractType(newContractType);

          await handleChangeType(newContractType ?? null);
        }}
        onClose={() => setOpenPopper(false)}
      />
      <AlineDialog
        classes={{ paper: classes.container }}
        open={Boolean(newContractType)}
        title="Create new contract type?"
        onClose={closeDialog}
      >
        <DialogContent className={classes.dialogContent}>
          <Typography variant="subtitle1">The following contract type will be created:</Typography>
          <div>
            <ContractTypeChip contractType={newContractType} />
          </div>
        </DialogContent>
        <DialogActions>
          <AlineButton
            color="primary"
            disabled={creatingType}
            variant="outlined"
            onClick={closeDialog}
          >
            Cancel
          </AlineButton>
          <AlineTooltip placement="bottom" title={disabledReason}>
            <div className={classes.saveButton}>
              <AlineButton
                color="primary"
                disabled={Boolean(disabledReason)}
                loading={creatingType}
                variant="contained"
                onClick={handleCreateNewType}
              >
                Add Contract Type
              </AlineButton>
            </div>
          </AlineTooltip>
        </DialogActions>
      </AlineDialog>
    </>
  );
}

export function useContractTypePicker({ document }: ContractTypePickerProps) {
  const currentUser = useSelectCurrentUser();
  const { updateDocumentType } = useUpdateDocumentType();
  const { getIsAuthorizedToEditStorable } = useGetIsAuthorizedToEditStorable();
  const { canEdit } = getIsAuthorizedToEditStorable(document);
  const [openPopper, setOpenPopper] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { createContractTypes, loading: creatingType } = useCreateContractTypes();
  const [newContractType, setNewContractType] = useState<NewContractType | null>(null);
  const disabledReason =
    currentUser?.role !== Apollo.OrgRole.Admin
      ? 'Only admins can create contract types'
      : !canEdit
        ? 'Only editors and the document owner can edit contract type'
        : '';

  function closeDialog(e?: React.MouseEvent): void {
    e?.stopPropagation();
    setNewContractType(null);
  }

  async function handleChangeType(value: ExistingContractType | null): Promise<void> {
    await updateDocumentType({
      optimisticType: value,
      variables: { input: { contractTypeId: value?.id ?? null, documentId: document.id } },
    });
  }

  async function handleCreateNewType(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();

    if (!newContractType) return;

    const { data } = await createContractTypes({
      variables: { input: [{ value: newContractType.value }] },
    });
    const createdType = data?.createContractTypes.at(0);
    if (createdType) await handleChangeType(createdType);

    closeDialog(e);
  }

  return {
    anchorEl,
    closeDialog,
    creatingType,
    disabledReason,
    handleChangeType,
    handleCreateNewType,
    newContractType,
    openPopper,
    setAnchorEl,
    setNewContractType,
    setOpenPopper,
  };
}
